/**
 * Learn block writer — a marker-delimited section in an instructions file.
 *
 *   <!-- vg:learn:begin -->
 *   ## Learned Patterns
 *   *Auto-generated by `vg install --learn` on YYYY-MM-DD — do not edit manually*
 *
 *   ### Section
 *   *~1,234 tokens/session saved*
 *   - bullet
 *
 *   <!-- vg:learn:end -->
 *
 * The marker pair is distinct from `vg install`'s `<!-- vg:begin -->` block
 * (which `vg install` strips and refreshes). Re-runs replace re-emitted
 * sections wholesale and carry un-re-emitted prior sections forward, so a
 * second identical run is a byte-for-byte no-op.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import type { Digest, Rule } from './types.js';

export const LEARN_BEGIN = '<!-- vg:learn:begin -->';
export const LEARN_END = '<!-- vg:learn:end -->';
export const LEARN_HEADING = '## Learned Patterns';
export const DEFAULT_LEARN_TARGET = 'CLAUDE.local.md';
export const KNOWN_LEARN_TARGETS: readonly string[] = ['CLAUDE.local.md', 'CLAUDE.md', 'AGENTS.md', 'GEMINI.md', 'GROK.md'];

const TOKENS_ANNOTATION_RE = /^\*~([\d,]+) tokens\/session saved\*\n?/;

export interface LearnSection {
  section: string;
  content: string;
  tokensSaved: number;
}

function isoDate(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

/** Render sections into the marker block (no trailing newline). */
export function renderLearnSections(sections: readonly LearnSection[], opts: { now?: number } = {}): string {
  const lines = [LEARN_BEGIN, LEARN_HEADING, `*Auto-generated by \`vg install --learn\` on ${isoDate(opts.now ?? Date.now())} — do not edit manually*`, ''];
  for (const s of sections) {
    lines.push(`### ${s.section}`);
    if (s.tokensSaved > 0) lines.push(`*~${fmt(s.tokensSaved)} tokens/session saved*`);
    lines.push(s.content.trimEnd());
    lines.push('');
  }
  lines.push(LEARN_END);
  return lines.join('\n');
}

export function rulesToSections(rules: readonly Rule[]): LearnSection[] {
  const seen = new Map<string, LearnSection>();
  for (const r of rules) {
    const cur = seen.get(r.section);
    if (cur) {
      cur.content = `${cur.content}\n${r.content.trim()}`;
      cur.tokensSaved = Math.max(cur.tokensSaved, r.estimatedTokensSaved);
    } else seen.set(r.section, { section: r.section, content: r.content.trim(), tokensSaved: r.estimatedTokensSaved });
  }
  return [...seen.values()];
}

/** The block for a digest (context rules first, then memory rules). */
export function renderLearnBlock(digest: Digest, opts: { now?: number } = {}): string {
  const ordered = [...digest.rules.filter((r) => r.target === 'context'), ...digest.rules.filter((r) => r.target !== 'context')];
  return renderLearnSections(rulesToSections(ordered), opts);
}

/** The raw marker block (markers included) inside `text`, or null. */
export function extractLearnBlock(text: string): string | null {
  const start = text.indexOf(LEARN_BEGIN);
  if (start < 0) return null;
  const end = text.indexOf(LEARN_END, start + LEARN_BEGIN.length);
  if (end < 0) return null;
  return text.slice(start, end + LEARN_END.length);
}

/** Sections parsed from a block (or a file containing one). */
export function parseLearnSections(text: string): LearnSection[] {
  const block = extractLearnBlock(text);
  if (!block) return [];
  const inner = block.slice(LEARN_BEGIN.length, block.length - LEARN_END.length);
  const out: LearnSection[] = [];
  const parts = `\n${inner}`.split('\n### ');
  for (const part of parts.slice(1)) {
    const nl = part.indexOf('\n');
    const heading = (nl < 0 ? part : part.slice(0, nl)).trim();
    let body = nl < 0 ? '' : part.slice(nl + 1);
    if (!heading) continue;
    let tokens = 0;
    const m = TOKENS_ANNOTATION_RE.exec(body);
    if (m) {
      tokens = Number(m[1].replace(/,/g, '')) || 0;
      body = body.slice(m[0].length);
    }
    out.push({ section: heading, content: body.trimEnd(), tokensSaved: tokens });
  }
  return out;
}

/** Remove the block, tidying blank lines; '' when nothing else remained. */
export function stripLearnBlock(text: string): string {
  const block = extractLearnBlock(text);
  if (!block) return text;
  const cleaned = text.replace(block, '').replace(/\n{3,}/g, '\n\n').trim();
  return cleaned ? `${cleaned}\n` : '';
}

/** New sections win; prior sections whose headings do not reappear are carried forward. */
export function mergeLearnSections(next: readonly LearnSection[], prior: readonly LearnSection[]): LearnSection[] {
  const names = new Set(next.map((s) => s.section));
  return [...next, ...prior.filter((p) => !names.has(p.section))];
}

/** Replace an existing block in `existing`, or append one; '' existing → block alone. */
export function mergeLearnBlock(existing: string, block: string): string {
  const old = extractLearnBlock(existing);
  if (old) return existing.replace(old, block);
  if (!existing.trim()) return `${block}\n`;
  return `${existing.trimEnd()}\n\n${block}\n`;
}

/** Read as UTF-8 with replacement; '' when absent. */
function readTolerant(file: string): { text: string; exists: boolean } {
  try {
    const buf = fs.readFileSync(file);
    return { text: buf.toString('utf8'), exists: true };
  } catch {
    return { text: '', exists: false };
  }
}

// ---------------------------------------------------------------------------
// Diff
// ---------------------------------------------------------------------------

const DIFF_MAX_LINES = 4000;

/** Minimal unified diff (LCS-based, whole-file hunk); '' when identical. */
export function unifiedDiff(before: string, after: string, label: string): string {
  if (before === after) return '';
  const a = before.split('\n');
  const b = after.split('\n');
  if (a.length > DIFF_MAX_LINES || b.length > DIFF_MAX_LINES) return `--- ${label}\n+++ ${label}\n@@ large change: ${a.length} → ${b.length} lines @@\n`;
  // LCS table (bounded by DIFF_MAX_LINES²).
  const n = a.length;
  const m = b.length;
  const dp: Uint16Array[] = [];
  for (let i = 0; i <= n; i++) dp.push(new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const lines: string[] = [`--- ${label}`, `+++ ${label}`, `@@ -1,${n} +1,${m} @@`];
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      lines.push(` ${a[i]}`);
      i++;
      j++;
    } else if (j < m && (i >= n || dp[i][j + 1] >= dp[i + 1][j])) {
      lines.push(`+${b[j]}`);
      j++;
    } else {
      lines.push(`-${a[i]}`);
      i++;
    }
  }
  return `${lines.join('\n')}\n`;
}

export interface WriteLearnResult {
  changed: boolean;
  created: boolean;
  diff: string;
  /** The file content after the write (or as it would be with `dryRun`). */
  content: string;
  path: string;
}

/**
 * Merge `block` into `file` (replace the existing block, carrying forward
 * prior sections the new block does not re-emit; append when absent; create
 * the file when missing). Idempotent: writing the same block twice yields the
 * same bytes. `dryRun` computes everything and touches nothing.
 */
export function writeLearnBlock(file: string, block: string, opts: { dryRun?: boolean } = {}): WriteLearnResult {
  const target = path.resolve(file);
  const { text: existing, exists } = readTolerant(target);
  const prior = parseLearnSections(existing);
  const next = parseLearnSections(block);
  let finalBlock = block;
  if (prior.length) {
    const merged = mergeLearnSections(next, prior);
    if (merged.length !== next.length) {
      // Re-render with carried-forward sections, keeping the new block's date line.
      const dateLine = block.split('\n').find((l) => l.startsWith('*Auto-generated by')) ?? '';
      finalBlock = renderLearnSections(merged).replace(/^\*Auto-generated by[^\n]*$/m, dateLine);
    }
  }
  const content = mergeLearnBlock(existing, finalBlock);
  const changed = content !== existing;
  const diff = unifiedDiff(existing, content, path.basename(target));
  if (changed && !opts.dryRun) {
    fs.mkdirSync(path.dirname(target), { recursive: true });
    const tmp = `${target}.${process.pid}.tmp`;
    fs.writeFileSync(tmp, content, 'utf8');
    fs.renameSync(tmp, target);
  }
  return { changed, created: !exists, diff, content, path: target };
}

/** Resolve `--target`: a known basename or a path, relative to `root`. */
export function resolveLearnTarget(target: string | undefined, root: string, env: NodeJS.ProcessEnv = process.env): string {
  const chosen = (target && target.trim()) || env.VG_LEARN_TARGET?.trim() || DEFAULT_LEARN_TARGET;
  return path.isAbsolute(chosen) ? chosen : path.join(root, chosen);
}
